import React, { useMemo, useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { motion, useReducedMotion } from "framer-motion";
import { Reveal } from "@/components/animation/Reveal";
import { staggerContainer, fadeItem } from "@/lib/motion";
import { MotionButton } from "@/components/motion/MotionButton";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { OrbitingCircles } from "@/registry/magicui/orbiting-circles";
import { AnimatedBeam } from "@/registry/magicui/animated-beam";
import { Globe } from "@/registry/magicui/globe";
import { Android } from "@/registry/magicui/android";
import { cn } from "@/lib/utils";

// ColorfulV3: restrained, white background, humanist body font,
// keeps WhimsicalV2's H1 font. Subtle motion + sound-wave motif.
export default function ColorfulV3() {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("1");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const rootRef = useRef<HTMLDivElement>(null);
  const rm = useReducedMotion();
  const panelContainer = useMemo(() => staggerContainer(rm, 0.08, 0.05), [rm]);
  const panelItem = useMemo(() => fadeItem(rm, 18), [rm]);
  const gridContainer = panelContainer;
  const gridItem = useMemo(() => fadeItem(rm, 16), [rm]);

  const handleCallMe = async () => {
    if (!phoneNumber) return;
    
    setIsSubmitting(true);
    
    try {
      // Format the full phone number with country code
      const fullPhoneNumber = `+${countryCode}${phoneNumber}`;
      
      console.log("Initiating call to:", fullPhoneNumber);
      
      // Call our edge function to log the call request
      const { data, error } = await supabase.functions.invoke('initiate-call', {
        body: {
          to_number: fullPhoneNumber,
          first_message: "Hello, this is HeyVail calling. I'd like to discuss how unifying all of your customer conversations and automating followup can scale your business but not your headcount."
        }
      });

      console.log("Edge function response:", data, error);

      if (error) {
        console.error("Error initiating call:", error);
        toast({
          title: "Error",
          description: "There was a problem initiating your call. Please try again.",
          variant: "destructive"
        });
        return;
      }

      // Success path
      toast({
        title: "Call Requested",
        description: "We'll call you shortly!",
      });

      // Reset phone number after successful submission
      setPhoneNumber("");
    } catch (error) {
      console.error("Error submitting call request:", error);
      toast({
        title: "Error",
        description: "There was a problem processing your request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Framer Motion handles reveals; reduced motion respected via useReducedMotion().

  return (
    <div ref={rootRef} className="cv3-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Work+Sans:wght@400;500;600;700;800;900&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Climate+Crisis&display=swap');

        :root{
          --bg: #ffffff;
          --ink: #111827; /* slate-900 */
          --muted: #475569; /* slate-600 */
          --ring: rgba(17,24,39,.08);
          /* Accent tints inspired by SuperHi card colors, bold and vibrant */
          --lilac-bg: #E0E7FF;
          --lilac-ink: #111827;
          --green-bg: #34D399;
          --green-ink: #FFFFFF;
          --orange-bg: #FED7AA;
          --orange-ink: #FFFFFF;
        }

        .cv3-root{
          background: var(--bg);
          color: var(--ink);
          font-family: 'Work Sans', system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
          min-height: 100vh;
        }

        /* Three-column grid system for consistent layout */
        .cv3-grid-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 48px 20px;
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 40px;
          align-items: center;
        }

        .cv3-grid-content {
          grid-column: span 2;
        }

        .cv3-grid-content-right {
          grid-column: 2 / span 2;
        }

        .cv3-grid-placeholder {
          background: transparent;
          border: none;
          border-radius: 12px;
          height: 600px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(0, 0, 0, 0.4);
          font-style: italic;
        }

        .cv3-hero-graphic {
          position: relative;
          height: 400px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          margin-left: -20px;
        }

        /* Keep existing widths for backwards compatibility */
        .cv3-text-width { max-width: 800px; margin: 0 auto; padding: 48px 20px; }
        .cv3-card-width { max-width: 1200px; margin: 0 auto; padding: 48px 20px; }


        .cv3-wrap{ max-width: 1200px; margin: 0 auto; padding: 48px 20px; }

        .cv3-nav{ display:flex; align-items:center; justify-content:center; gap:16px; margin-bottom: 36px; }
        .cv3-logo{
          font-family: 'Climate Crisis', 'Work Sans', system-ui, -apple-system, sans-serif;
          font-weight: 800;
          letter-spacing: .2px;
          text-transform: uppercase;
          position: relative;
          display: inline-block;
          font-size: 36px;
        }
        .cv3-actions{ display:flex; gap:8px }
        .cv3-btn{ border-radius: 999px; padding: 10px 16px; border: 1px solid var(--ring); background:#fff; font-weight:600 }
        .cv3-btn:hover{ box-shadow: 0 2px 0 rgba(0,0,0,.04), 0 8px 24px rgba(0,0,0,.06) }

        .cv3-hero{ padding: 48px 0; text-align: center; }
        .cv3-h1{
          font-family: 'Work Sans', system-ui, -apple-system, sans-serif;
          font-weight: 900; /* heaviest Work Sans weight */
          font-size: clamp(36px, 7vw, 64px);
          line-height: .98;
          letter-spacing: .004em;
          margin: 0 0 16px;
        }

        /* Audio bar animation for HeyVAIL */
        .cv3-vail-container {
          position: relative;
          display: inline-block;
        }

        .cv3-vail-text {
          position: relative;
          z-index: 2;
          color: transparent;
          -webkit-text-stroke: 2px #111827;
          text-stroke: 2px #111827;
          font-family: 'Climate Crisis', 'Work Sans', system-ui, -apple-system, sans-serif;
        }

        .cv3-hey-text {
          font-family: 'Climate Crisis', 'Work Sans', system-ui, -apple-system, sans-serif;
        }

        /* Small animated bars for nav logo */
        .cv3-logo-vail {
          position: relative;
          display: inline-block;
        }

        .cv3-logo-vail-text {
          position: relative;
          z-index: 2;
          color: transparent;
          -webkit-text-stroke: 1px #111827;
          text-stroke: 1px #111827;
        }

        .cv3-logo-audio-bars {
          position: absolute;
          top: 50%;
          left: 45%;
          transform: translate(-50%, -50%);
          display: flex;
          align-items: center;
          gap: 1px;
          z-index: 1;
          opacity: 0.4;
        }

        .cv3-logo-bar {
          width: 2px;
          background: linear-gradient(45deg, #6366F1, #10B981, #FB923C);
          border-radius: 1px;
          animation: cv3-pulse linear infinite;
        }

        .cv3-logo-bar:nth-child(1) { height: 8px; animation-duration: 0.8s; animation-delay: 0s; }
        .cv3-logo-bar:nth-child(2) { height: 12px; animation-duration: 1.2s; animation-delay: 0.1s; }
        .cv3-logo-bar:nth-child(3) { height: 6px; animation-duration: 0.9s; animation-delay: 0.2s; }
        .cv3-logo-bar:nth-child(4) { height: 14px; animation-duration: 1.1s; animation-delay: 0.3s; }
        .cv3-logo-bar:nth-child(5) { height: 10px; animation-duration: 1.0s; animation-delay: 0.4s; }
        .cv3-logo-bar:nth-child(6) { height: 16px; animation-duration: 0.7s; animation-delay: 0.5s; }
        .cv3-logo-bar:nth-child(7) { height: 11px; animation-duration: 1.3s; animation-delay: 0.6s; }
        .cv3-logo-bar:nth-child(8) { height: 7px; animation-duration: 0.8s; animation-delay: 0.7s; }

        .cv3-audio-bars {
          position: absolute;
          top: 50%;
          left: 45%;
          transform: translate(-50%, -50%);
          display: flex;
          align-items: center;
          gap: 3px;
          z-index: 1;
          opacity: 0.3;
        }

        .cv3-bar {
          width: 4px;
          background: linear-gradient(45deg, #6366F1, #10B981, #FB923C);
          border-radius: 2px;
          animation: cv3-pulse linear infinite;
        }

        .cv3-bar:nth-child(1) { height: 24px; animation-duration: 0.8s; animation-delay: 0s; }
        .cv3-bar:nth-child(2) { height: 36px; animation-duration: 1.2s; animation-delay: 0.1s; }
        .cv3-bar:nth-child(3) { height: 18px; animation-duration: 0.9s; animation-delay: 0.2s; }
        .cv3-bar:nth-child(4) { height: 42px; animation-duration: 1.1s; animation-delay: 0.3s; }
        .cv3-bar:nth-child(5) { height: 28px; animation-duration: 1.0s; animation-delay: 0.4s; }
        .cv3-bar:nth-child(6) { height: 52px; animation-duration: 0.7s; animation-delay: 0.5s; }
        .cv3-bar:nth-child(7) { height: 32px; animation-duration: 1.3s; animation-delay: 0.6s; }
        .cv3-bar:nth-child(8) { height: 20px; animation-duration: 0.8s; animation-delay: 0.7s; }
        .cv3-bar:nth-child(9) { height: 45px; animation-duration: 1.1s; animation-delay: 0.8s; }
        .cv3-bar:nth-child(10) { height: 35px; animation-duration: 0.9s; animation-delay: 0.9s; }
        .cv3-bar:nth-child(11) { height: 22px; animation-duration: 1.2s; animation-delay: 1.0s; }
        .cv3-bar:nth-child(12) { height: 38px; animation-duration: 0.8s; animation-delay: 1.1s; }
        .cv3-bar:nth-child(13) { height: 26px; animation-duration: 1.0s; animation-delay: 1.2s; }
        .cv3-bar:nth-child(14) { height: 48px; animation-duration: 0.9s; animation-delay: 1.3s; }
        .cv3-bar:nth-child(15) { height: 30px; animation-duration: 1.1s; animation-delay: 1.4s; }
        .cv3-bar:nth-child(16) { height: 40px; animation-duration: 0.8s; animation-delay: 1.5s; }
        .cv3-bar:nth-child(17) { height: 25px; animation-duration: 1.3s; animation-delay: 1.6s; }
        .cv3-bar:nth-child(18) { height: 33px; animation-duration: 0.7s; animation-delay: 1.7s; }
        .cv3-bar:nth-child(19) { height: 29px; animation-duration: 1.0s; animation-delay: 1.8s; }
        .cv3-bar:nth-child(20) { height: 41px; animation-duration: 0.9s; animation-delay: 1.9s; }
        .cv3-bar:nth-child(21) { height: 23px; animation-duration: 1.2s; animation-delay: 2.0s; }
        .cv3-bar:nth-child(22) { height: 37px; animation-duration: 0.8s; animation-delay: 2.1s; }
        .cv3-bar:nth-child(23) { height: 31px; animation-duration: 1.1s; animation-delay: 2.2s; }
        .cv3-bar:nth-child(24) { height: 27px; animation-duration: 0.9s; animation-delay: 2.3s; }

        @keyframes cv3-pulse {
          0%, 100% { transform: scaleY(0.3); opacity: 0.3; }
          50% { transform: scaleY(1); opacity: 0.7; }
        }

        @media (prefers-reduced-motion: reduce) {
          .cv3-bar { animation: none; transform: scaleY(0.6); }
        }
        .cv3-sub{ color: var(--muted); font-size: clamp(16px, 2.1vw, 18px); line-height: 1.6; margin: 0 auto 32px; }

        /* Phone input form styling */
        .cv3-cta-form {
          display: flex;
          gap: 12px;
          margin: 0 auto 16px;
          align-items: stretch;
        }

        .cv3-phone-input {
          display: flex;
          background: white;
          border-radius: 50px;
          border: 1px solid var(--ring);
          flex: 1;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,.04);
        }

        .cv3-country-code {
          background: transparent;
          border: none;
          padding: 16px 16px 16px 20px;
          font-size: 14px;
          font-weight: 500;
          color: var(--muted);
          outline: none;
          cursor: pointer;
        }

        .cv3-phone-field {
          background: transparent;
          border: none;
          padding: 16px 20px 16px 8px;
          flex: 1;
          font-size: 15px;
          outline: none;
        }

        .cv3-phone-field::placeholder {
          color: var(--muted);
        }

        .cv3-cta-button {
          background: #FB923C;
          color: white;
          border: none;
          border-radius: 50px;
          padding: 16px 28px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          white-space: nowrap;
          box-shadow: 0 2px 8px rgba(251,146,60,.2);
          transition: all 0.2s ease;
        }

        .cv3-cta-button:hover {
          background: #EA580C;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(251,146,60,.3);
        }

        .cv3-legal {
          color: var(--muted);
          font-size: 13px;
          text-align: center;
        }

        /* Mobile responsive styles for CTA form */
        @media (max-width: 768px) {
          .cv3-cta-form {
            flex-direction: column;
            gap: 16px;
            max-width: 100%;
          }

          .cv3-phone-input {
            width: 100%;
          }

          .cv3-cta-button {
            width: 100%;
            text-align: center;
            padding: 18px 20px;
            font-size: 16px;
          }

          .cv3-cta-content {
            padding: 32px 20px;
            margin: 0 16px;
          }

          .cv3-final-cta {
            padding: 60px 0;
          }
        }
          margin-top: 12px;
        }

        .cv3-legal a {
          color: var(--ink);
          text-decoration: none;
          font-weight: 600;
        }

        .cv3-legal a:hover {
          text-decoration: underline;
        }

        /* Unified Communication Section */
        .cv3-unified-comm {
          padding: 80px 0;
          background: #ffffff;
        }

        .cv3-comm-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 32px;
          margin-bottom: 80px;
          align-items: stretch;
        }


        @media (max-width: 980px) {
          .cv3-comm-grid {
            grid-template-columns: 1fr;
          }
        }

        .cv3-comm-card {
          padding: 40px 48px;
          border-radius: 24px;
          background: white;
          border: 1px solid var(--ring);
          text-align: center;
          box-shadow: 0 4px 20px rgba(0,0,0,.04);
          height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .cv3-comm-card.green {
          background: var(--green-bg);
        }
        .cv3-comm-card.green h3 {
          color: var(--ink);
        }

        .cv3-comm-card.orange {
          background: var(--orange-bg);
        }
        .cv3-comm-card.orange h3 {
          color: var(--ink);
        }

        .cv3-comm-card.blue {
          background: #E0E7FF;
        }
        .cv3-comm-card.blue h3 {
          color: var(--ink);
        }

        .cv3-comm-card.is-visible {
          transform: translateY(0) scale(1);
          opacity: 1;
        }

        .cv3-comm-card:hover {
          box-shadow: 0 8px 40px rgba(0,0,0,.08);
          transform: translateY(-2px) scale(1);
        }

        .cv3-comm-icon {
          margin-bottom: 24px;
          display: flex;
          justify-content: center;
        }

        .cv3-comm-card h3 {
          font-size: clamp(20px, 3vw, 28px);
          font-weight: 800;
          color: var(--ink);
          margin-bottom: 16px;
          line-height: 1.2;
        }

        .cv3-comm-card p {
          color: var(--muted);
          line-height: 1.5;
          font-size: 16px;
        }

        .cv3-beyond-section {
          display: grid;
          grid-template-columns: 1.1fr 0.9fr;
          gap: 60px;
          align-items: center;
        }

        .cv3-beyond-content h2 {
          font-size: clamp(28px, 5vw, 42px);
          font-weight: 800;
          color: var(--ink);
          margin-bottom: 16px;
          line-height: 1.1;
        }

        .cv3-beyond-subtitle {
          font-size: 18px;
          color: var(--ink);
          font-weight: 600;
          margin-bottom: 20px;
        }

        .cv3-beyond-content > p {
          color: var(--muted);
          line-height: 1.6;
          font-size: 16px;
          margin-bottom: 32px;
        }

        .cv3-beyond-features {
          display: grid;
          gap: 24px;
        }

        .cv3-beyond-feature {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          padding: 20px;
          border-radius: 16px;
          background: rgba(255,255,255,0.5);
          border: 1px solid rgba(255,255,255,0.2);
          backdrop-filter: blur(10px);
        }

        .cv3-beyond-icon {
          font-size: 28px;
          flex-shrink: 0;
        }

        .cv3-beyond-feature h4 {
          font-size: 18px;
          font-weight: 700;
          color: var(--ink);
          margin-bottom: 8px;
        }

        .cv3-beyond-feature p {
          color: var(--muted);
          line-height: 1.5;
          font-size: 15px;
        }

        /* How It Works Section */
        .cv3-how-it-works {
          padding: 80px 0;
          background: linear-gradient(to bottom, #ffffff 0%, #fafafa 100%);
        }

        .cv3-section-header {
          text-align: center;
          margin-bottom: 60px;
        }

        .cv3-kicker {
          display: inline-block;
          background: var(--lilac-bg);
          color: var(--lilac-ink);
          padding: 8px 16px;
          border-radius: 50px;
          font-size: 14px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 20px;
        }

        .cv3-section-title {
          font-family: 'Work Sans', system-ui, -apple-system, sans-serif;
          font-weight: 900;
          font-size: clamp(28px, 5vw, 48px);
          line-height: 1.1;
          color: var(--ink);
          margin-bottom: 20px;
          text-transform: uppercase;
        }

        .cv3-section-subtitle {
          font-size: clamp(16px, 2vw, 20px);
          color: var(--muted);
          line-height: 1.6;
          margin: 0 auto;
        }

        .cv3-process-steps {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 32px;
          margin-bottom: 80px;
        }

        .cv3-step {
          text-align: center;
          padding: 32px 24px;
          border-radius: 20px;
          background: white;
          border: 1px solid var(--ring);
        }

        .cv3-step.is-visible {
          transform: translateY(0);
          opacity: 1;
        }

        .cv3-step-number {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          background: var(--ink);
          color: white;
          border-radius: 50%;
          font-size: 16px;
          font-weight: 700;
          margin-bottom: 16px;
        }

        .cv3-step-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
        }

        .cv3-step h3 {
          font-size: 20px;
          font-weight: 700;
          color: var(--ink);
          margin-bottom: 12px;
          line-height: 1.3;
        }

        .cv3-step p {
          color: var(--muted);
          line-height: 1.6;
          font-size: 15px;
        }

        .cv3-step-card {
          height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .cv3-step-number {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.9);
          color: var(--ink);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          font-weight: 800;
          margin-bottom: 20px;
        }

        .cv3-details-grid {
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 60px;
          align-items: flex-start;
        }

        .cv3-details-content h3 {
          font-size: clamp(24px, 4vw, 36px);
          font-weight: 700;
          color: var(--ink);
          margin-bottom: 32px;
          line-height: 1.2;
        }

        .cv3-feature-list {
          display: grid;
          gap: 24px;
        }

        .cv3-feature-item {
          display: flex;
          align-items: flex-start;
          gap: 16px;
        }

        .cv3-feature-icon {
          width: 24px;
          height: 24px;
          background: var(--green-bg);
          color: var(--green-ink);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          flex-shrink: 0;
        }

        .cv3-feature-item h4 {
          font-size: 16px;
          font-weight: 700;
          color: var(--ink);
          margin-bottom: 4px;
        }

        .cv3-feature-item p {
          font-size: 14px;
          color: var(--muted);
          line-height: 1.5;
        }

        /* Final CTA Section */
        .cv3-final-cta {
          padding: 80px 0;
          background: linear-gradient(135deg, var(--lilac-bg) 0%, var(--green-bg) 50%, var(--orange-bg) 100%);
        }

        .cv3-cta-content {
          text-align: center;
          margin: 0 auto;
          padding: 48px 32px;
          background: rgba(255, 255, 255, 0.95);
          border-radius: 32px;
          border: 1px solid var(--ring);
          backdrop-filter: blur(20px);
        }

        .cv3-cta-content h2 {
          font-size: clamp(28px, 5vw, 40px);
          font-weight: 700;
          color: var(--ink);
          margin-bottom: 20px;
          line-height: 1.2;
        }

        .cv3-cta-content > p {
          color: var(--muted);
          line-height: 1.6;
          margin-bottom: 32px;
          font-size: clamp(15px, 2vw, 18px);
        }

        @media (max-width: 980px) {
          .cv3-grid-container {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .cv3-grid-content,
          .cv3-grid-content-right {
            grid-column: span 1;
          }

          .cv3-grid-placeholder {
            height: 250px;
            order: -1;
            margin-bottom: 20px;
          }

          .cv3-details-grid {
            grid-template-columns: 1fr;
            gap: 40px;
          }

          .cv3-process-steps {
            grid-template-columns: 1fr;
            gap: 24px;
          }

          .cv3-comm-grid {
            grid-template-columns: 1fr;
            gap: 24px;
            margin-bottom: 60px;
          }

          .cv3-beyond-section {
            grid-template-columns: 1fr;
            gap: 40px;
          }

          .cv3-beyond-visual {
            order: -1;
          }
        }

        /* Subtle wave motif under hero title */
        .waves{ position: relative; height: 90px; margin-top: 18px }
        .waves svg{ position:absolute; inset:0; width:100%; height:100% }
        .wave-line{ stroke: currentColor; opacity: .22; stroke-width: 2; fill: none; stroke-linecap: round; stroke-dasharray: 6 10; animation: wave-move 10s linear infinite }
        .wave-1{ color: #6366F1; animation-duration: 12s }
        .wave-2{ color: #10B981; animation-duration: 10s; animation-direction: reverse }
        .wave-3{ color: #FB923C; animation-duration: 14s }
        @keyframes wave-move{ to { stroke-dashoffset: -2000; } }
        @media (prefers-reduced-motion: reduce){ .wave-line{ animation: none } }

        /* Card grid with restrained motion */
        .cv3-panels{ display:grid; grid-template-columns: repeat(12, 1fr); gap: 18px; margin-top: 40px }

        .panel{ grid-column: span 4; border-radius: 28px; padding: 24px 36px; border: 1px solid var(--ring); background: #fff; position:relative; overflow:hidden; height: 100%; display: flex; flex-direction: column; justify-content: space-between; }
        .panel.is-visible{ transform: translateY(0) scale(1); opacity: 1 }
        .panel:focus-within, .panel:hover{ box-shadow: 0 2px 0 rgba(0,0,0,.04), 0 18px 60px rgba(0,0,0,.08) }
        .panel h3{ font-weight: 800; font-size: 28px; line-height: 1.05; margin: 6px 0 14px }
        .panel p{ color: var(--muted); line-height: 1.5 }
        .panel .cta{ margin-top: 16px; display:inline-flex; align-items:center; gap:8px; font-weight: 700 }

        /* Colored corners reminiscent of SuperHi, but subtler */
        .panel.lilac{ background: var(--lilac-bg) }
        .panel.lilac h3{ color: var(--lilac-ink) }
        .panel.blue{ background: #E0E7FF }
        .panel.blue h3{ color: var(--ink) }
        .panel.green{ background: var(--green-bg) }
        .panel.green h3{ color: var(--ink) }
        .panel.orange{ background: var(--orange-bg) }
        .panel.orange h3{ color: var(--ink) }

        .badge{ position:absolute; right: 14px; top: 14px; background: #111827; color:#fff; font-size: 12px; letter-spacing:.04em; padding:6px 10px; border-radius:999px; font-weight:700; opacity:.9 }

        .cv3-foot{ margin: 64px 0 28px; color: var(--muted); font-size: 14px }

        @media (max-width: 980px){ .cv3-hero{ grid-template-columns: 1fr } .cv3-panels{ grid-template-columns: 1fr; } .panel{ grid-column: auto } }
      `}</style>

      <div className="cv3-wrap">
        <header className="cv3-nav">
          <div className="cv3-logo">
            hey<span className="cv3-logo-vail">
              <span className="cv3-logo-vail-text">vail</span>
              <div className="cv3-logo-audio-bars">
                <div className="cv3-logo-bar"></div>
                <div className="cv3-logo-bar"></div>
                <div className="cv3-logo-bar"></div>
                <div className="cv3-logo-bar"></div>
                <div className="cv3-logo-bar"></div>
                <div className="cv3-logo-bar"></div>
                <div className="cv3-logo-bar"></div>
                <div className="cv3-logo-bar"></div>
              </div>
            </span>
          </div>
        </header>

        {/* Hero */}
        <Reveal as="section" className="cv3-hero">
          <div className="cv3-grid-container">
            <div className="cv3-grid-content">
              <h1 className="cv3-h1">Conversations <br/> grow your business.<br />HeyVail scales them.</h1>
              <p className="cv3-sub">
                Imagine a sales and customer service team that always follows the playbook, works 24 / 7, and covers email, calls, SMS, and website chat. <strong>Quit imagining</strong> and meet Vail, your full service answer to creating and scaling conversations. <br />
                Drop your number in and she'll give you a call right away.
              </p>

              {/* Phone input form */}
              <div className="cv3-cta-form">
                <div className="cv3-phone-input">
                  <Select value={countryCode} onValueChange={setCountryCode} disabled={isSubmitting}>
                    <SelectTrigger className="cv3-country-code w-[100px]">
                      <SelectValue placeholder="Code" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">us +1</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="tel"
                    placeholder="Enter your phone number"
                    className="cv3-phone-field"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    disabled={isSubmitting}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCallMe();
                      }
                    }}
                  />
                </div>
                <MotionButton
                  className="cv3-cta-button"
                  onClick={handleCallMe}
                  disabled={isSubmitting || !phoneNumber}
                  size="lg"
                >
                  {isSubmitting ? "Processing..." : "Call Me Now →"}
                </MotionButton>
              </div>

              <p className="cv3-legal">
                By submitting, you agree to our <a href="/terms-of-service">Terms</a> and <a href="/privacy-policy">Privacy Policy</a>
              </p>
            </div>

            <div className="cv3-hero-graphic">
              <Globe
                autoRotate={true}
                showMarkers={true}
                markerCount={12}
              />
            </div>
          </div>
        </Reveal>

        {/* Panels */}
        <Reveal as="section">
          <motion.div className="cv3-panels"
            variants={panelContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ amount: 0.2, once: true }}
          >
            <motion.div
              variants={panelItem}
              layout
              whileHover={rm ? undefined : { y: -2, scale: 1.02 }}
              whileTap={rm ? undefined : { scale: 0.985 }}
              transition={{ type: 'spring', stiffness: 280, damping: 22 }}
              style={{ gridColumn: 'span 4' }}
            >
              <Card className="panel lilac">
                <h3>Unified Communications</h3>
                <p>
                  Bring all your customer conversations into one platform. No more switching between apps or missing important messages.
                </p>
              </Card>
            </motion.div>

            <motion.div
              variants={panelItem}
              layout
              whileHover={rm ? undefined : { y: -2, scale: 1.02 }}
              whileTap={rm ? undefined : { scale: 0.985 }}
              transition={{ type: 'spring', stiffness: 280, damping: 22 }}
              style={{ gridColumn: 'span 4' }}
            >
              <Card className="panel green">
                <h3>AI-Powered Follow-ups</h3>
                <p>
                  Never let a lead go cold again. Our AI automatically follows up with prospects and nurtures them until they're sales-ready.
                </p>
              </Card>
            </motion.div>

            <motion.div
              variants={panelItem}
              layout
              whileHover={rm ? undefined : { y: -2, scale: 1.02 }}
              whileTap={rm ? undefined : { scale: 0.985 }}
              transition={{ type: 'spring', stiffness: 280, damping: 22 }}
              style={{ gridColumn: 'span 4' }}
            >
              <Card className="panel orange">
                <h3>Your Full Service team</h3>
                <p>
                  Vail isn't just AI technology. It's also a team of humans who make it all work for you. You don't have to know how to connect HeyVail to your CRM or your cash registers. We do.
                </p>
              </Card>
            </motion.div>
          </motion.div>
        </Reveal>

        {/* Unified Communication Section */}
        <section className="cv3-unified-comm">
          <div className="cv3-grid-container">
            <div className="cv3-grid-placeholder">
              <div className="scale-50 md:scale-75 lg:scale-100">
                <Android
                  className="mx-auto"
                  videoSrc="/videos/heyvail-homepage.mp4"
                />
              </div>
            </div>

            <div className="cv3-grid-content-right">
              <div className="cv3-section-header">
                <h2 className="cv3-section-title">Everyone tells you AI will make you millions, but no one&apos;s telling you how</h2>
                <p className="cv3-section-subtitle">
                  Here&apos;s a fact: 90% of all AI implementations fail to show any positive ROI, because most of them are executed by people who&apos;ve never done them before. We&apos;s your full-service experts at this: our founding team already includes a successful AI-startup exit, and we know how to implement AI + traditional technology and even real human effort together to create what you really want: outcomes for your business.
                </p>

                {/* Phone CTA */}
                <div className="cv3-cta-form" style={{ marginTop: '32px' }}>
                  <div className="cv3-phone-input">
                    <Select value={countryCode} onValueChange={setCountryCode} disabled={isSubmitting}>
                      <SelectTrigger className="cv3-country-code w-[100px]">
                        <SelectValue placeholder="Code" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">us +1</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="tel"
                      placeholder="Enter your phone number"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      disabled={isSubmitting}
                      className="cv3-phone-field"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleCallMe();
                        }
                      }}
                    />
                  </div>
                  <MotionButton
                    className="cv3-cta-button"
                    onClick={handleCallMe}
                    disabled={isSubmitting || !phoneNumber}
                    size="lg"
                  >
                    {isSubmitting ? "Processing..." : "Call Me Now →"}
                  </MotionButton>
                </div>
                <p className="cv3-legal" style={{ marginTop: '16px' }}>
                  By submitting, you agree to our <a href="/terms-of-service">Terms</a> and <a href="/privacy-policy">Privacy Policy</a>
                </p>
              </div>
            </div>
          </div>

          <div className="cv3-card-width" style={{ paddingTop: 0 }}>
            <motion.div
              className="cv3-comm-grid"
              variants={gridContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ amount: 0.2, once: true }}
            >
              <motion.div variants={gridItem} whileHover={rm ? undefined : { y: -2, scale: 1.01 }}>
                <Card className="cv3-comm-card green">
                  <div className="cv3-comm-icon">
                  <div style={{
                    width: 48,
                    height: 48,
                    background: 'white',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--green-ink)',
                    fontSize: '20px'
                  }}>💬</div>
                </div>
                  <h3>For sales. For customer service. For appointments.</h3>
                  <p>
                    Anywhere your business needs to handle conversations, you need someone dependable. For the last few years your choices have been pretty bad: pay a lot for someone local who has to be trained and will probably quit in 6 months or hire overseas, pay less, and hope your customers don't mind.
                  </p>
                </Card>
              </motion.div>

              <motion.div variants={gridItem} whileHover={rm ? undefined : { y: -2, scale: 1.01 }}>
                <Card className="cv3-comm-card orange">
                  <div className="cv3-comm-icon">
                  <div style={{
                    width: 48,
                    height: 48,
                    background: 'white',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--orange-ink)',
                    fontSize: '20px'
                  }}>⚡</div>
                </div>
                  <h3>Automate Everything After the Conversation</h3>
                  <p>
                    When customers ask to schedule meetings, need documents collected, want package updates, or have complaints—Vail handles it all automatically.
                    Scale any workflow 24/7 without adding staff or missing requests.
                  </p>
                </Card>
              </motion.div>

              <motion.div variants={gridItem} whileHover={rm ? undefined : { y: -2, scale: 1.01 }}>
                <Card className="cv3-comm-card blue">
                  <div className="cv3-comm-icon">
                  <div style={{
                    width: 48,
                    height: 48,
                    background: 'white',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#3B82F6',
                    fontSize: '20px'
                  }}>📈</div>
                </div>
                  <h3>300% More Conversions</h3>
                  <p>
                    Your conversion rate goes up 300% if you call leads back in 30 seconds. Vail makes that happen automatically, every time, 24/7.
                  </p>
                </Card>
              </motion.div>
            </motion.div>

            {/* How does HeyVail work header and intro */}
            <div style={{ marginBottom: '60px' }}>
              <h2 style={{
                fontSize: 'clamp(32px, 5vw, 48px)',
                fontWeight: 800,
                color: 'var(--ink)',
                marginBottom: '16px',
                textAlign: 'center'
              }}>How does HeyVail work?</h2>
              <p style={{
                fontSize: '20px',
                color: 'var(--ink)',
                fontWeight: 600,
                textAlign: 'center',
                marginBottom: '12px'
              }}>
                We're experts at one thing: building with AI.
              </p>
              <p style={{
                fontSize: '16px',
                color: 'var(--muted)',
                textAlign: 'center',
                maxWidth: '800px',
                margin: '0 auto'
              }}>
                Every engagement starts with the same steps: map your business, connect your tools, and create AI workflows that work for you to move faster and scale.
              </p>
            </div>

            {/* 3 Step Cards */}
            <motion.div
              className="cv3-process-steps"
              variants={gridContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ amount: 0.2, once: true }}
              style={{ marginBottom: '80px' }}
            >
              {/* Step 1 */}
              <motion.div variants={gridItem} whileHover={rm ? undefined : { y: -2, scale: 1.01 }}>
                <Card className="panel lilac cv3-step-card">
                  <div className="cv3-step-number">1</div>
                  <h3>Someone Needs to Talk to You</h3>
                  <p>
                    A lead submits a form, a customer has a question, or someone needs support - they reach out through any channel.
                  </p>
                </Card>
              </motion.div>

              {/* Step 2 */}
              <motion.div variants={gridItem} whileHover={rm ? undefined : { y: -2, scale: 1.01 }}>
                <Card className="panel green cv3-step-card">
                  <div className="cv3-step-number">2</div>
                  <h3>Vail Calls in 30 Seconds</h3>
                  <p>
                    Vail immediately calls them back and has a natural conversation to understand exactly what they need.
                  </p>
                </Card>
              </motion.div>

              {/* Step 3 */}
              <motion.div variants={gridItem} whileHover={rm ? undefined : { y: -2, scale: 1.01 }}>
                <Card className="panel orange cv3-step-card">
                  <div className="cv3-step-number">3</div>
                  <h3>Vail Automates Next Steps</h3>
                  <p>
                    She schedules calls with humans, books appointments, or answers their questions live in the call - whatever moves things forward.
                  </p>
                </Card>
              </motion.div>
            </motion.div>

            {/* Text content and graphic in 3-column layout */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              gap: '60px',
              alignItems: 'flex-start'
            }}>
              {/* Text content spanning columns 1-2 */}
              <div className="cv3-beyond-features">
                <div className="cv3-beyond-feature">
                  <div className="cv3-beyond-icon">🗺️</div>
                  <div>
                    <h4>Map your business</h4>
                    <p>Every business is different and what we you can automate in one business is the most important thing to keep the human touch with in another. It's important we understand which is which for you so we make your customers happier, not feeling neglected.</p>
                  </div>
                </div>

                <div className="cv3-beyond-feature">
                  <div className="cv3-beyond-icon">🔗</div>
                  <div>
                    <h4>Connect your tools</h4>
                    <p>Connect to calendars, CRMs, payment systems—Vail works with what you already use. This is crucial, becuse a single source of truth around your customer and what makes them buy is the kind of thing that big companies pay consultants like McKinsey $100,000 to do for them.</p>
                  </div>
                </div>

                <div className="cv3-beyond-feature">
                  <div className="cv3-beyond-icon">🎨</div>
                  <div>
                    <h4>Create AI Workflows</h4>
                    <p>It's important you realize that AI *can't* replace every part of your business. We're not gonna tell you otherwise. But we will find the areas where conversations can naturally be handled by AI and save you time, money, and customer aggravation by making 24/7 communication core to your business.</p>
                  </div>
                </div>
              </div>

              {/* Animated graphic in column 3 */}
              <div className="cv3-beyond-visual">
                <div className="cv3-workflow-illustration" style={{ position: 'relative', width: '100%', height: '350px' }}>
                  <MagicAnimatedBeam />
                </div>
              </div>
            </div>
          </div>
        </section>


        {/* How It Works Section */}
        <section className="cv3-how-it-works">
          <div className="cv3-text-width">
            <div className="cv3-section-header">
              <span className="cv3-kicker">The System</span>
              <h2 className="cv3-section-title">Vail turns every conversation into results faster</h2>
              <p className="cv3-section-subtitle">
                It takes weeks to hire, train, and ramp up a new sales or customer service rep. Vail is ready to go in days, and scales to handle 10x more conversations than a human team could. Here's how it works:
              </p>
            </div>
          </div>

          <div className="cv3-card-width" style={{ paddingTop: 0 }}>
            <motion.div
              className="cv3-process-steps"
              variants={gridContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ amount: 0.2, once: true }}
            >
              <motion.div variants={gridItem} whileHover={rm ? undefined : { y: -2, scale: 1.01 }}>
                <Card className="panel blue cv3-step-card">
                <h3>Trained on your best practices</h3>
                <p>
                  We use advanced AI to build a custom model designed specifically for your business.
                </p>
                </Card>
              </motion.div>

              <motion.div variants={gridItem} whileHover={rm ? undefined : { y: -2, scale: 1.01 }}>
                <Card className="panel green cv3-step-card">
                <h3>Real Time Tracking</h3>
                <p>
                  Vail tracks every interaction in one place. From scheduling meetings to sending documents or updates, it connects to your tools and handles requests instantly.
                </p>
                </Card>
              </motion.div>

              <motion.div variants={gridItem} whileHover={rm ? undefined : { y: -2, scale: 1.01 }}>
                <Card className="panel orange cv3-step-card">
                <h3>24/7 Interaction</h3>
                <p>
                  After every conversation, Vail processes the next steps—helping Sales teams close more deals, supporting Customer Service with instant answers, and passing sales-ready prospects to your team with full context.
                </p>
                </Card>
              </motion.div>
            </motion.div>

            <div className="cv3-details-grid">
              <div className="cv3-details-visual">
                <div className="cv3-placeholder-visual" style={{ position: 'relative', width: '100%', height: '300px' }}>
                  <MagicOrbitingCircles />
                </div>
              </div>

              <div className="cv3-details-content">
                <h3>Complete customer intelligence in one system</h3>

                <div className="cv3-feature-list">
                  <div className="cv3-feature-item">
                    <div className="cv3-feature-icon">•</div>
                    <div>
                      <h4>All Channels, One Record</h4>
                      <p>SMS, email, phone, WhatsApp, chat—every interaction in one unified system</p>
                    </div>
                  </div>

                  <div className="cv3-feature-item">
                    <div className="cv3-feature-icon">•</div>
                    <div>
                      <h4>24/7 Processing</h4>
                      <p>Vail never sleeps, handles requests instantly, and escalates only when needed</p>
                    </div>
                  </div>

                  <div className="cv3-feature-item">
                    <div className="cv3-feature-icon">•</div>
                    <div>
                      <h4>Real-Time Tool Integration</h4>
                      <p>Connect to your existing systems—calendars, CRMs, payment processors</p>
                    </div>
                  </div>

                  <div className="cv3-feature-item">
                    <div className="cv3-feature-icon">•</div>
                    <div>
                      <h4>Complete Automation</h4>
                      <p>From first contact to deal closing—automate everything that doesn't need human touch</p>
                    </div>
                  </div>

                  <div className="cv3-feature-item">
                    <div className="cv3-feature-icon">•</div>
                    <div>
                      <h4>Scale Without Hiring</h4>
                      <p>Handle 10x more customer interactions without adding staff</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="cv3-final-cta">
          <div className="cv3-text-width">
            <div className="cv3-cta-content">
              <h2>Ready to see what Vail can really do?</h2>
              <p>
                Instantly experience our AI's voice capabilities on a live call.
              </p>

              <div className="cv3-cta-form">
                <div className="cv3-phone-input">
                  <select 
                    className="cv3-country-code"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    disabled={isSubmitting}
                  >
                    <option value="1">us +1</option>
                  </select>
                  <input
                    type="tel"
                    placeholder="Enter your phone number"
                    className="cv3-phone-field"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    disabled={isSubmitting}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCallMe();
                      }
                    }}
                  />
                </div>
                <MotionButton 
                  className="cv3-cta-button"
                  onClick={handleCallMe}
                  disabled={isSubmitting || !phoneNumber}
                >
                  {isSubmitting ? "Processing..." : "Have Vail Call You Now →"}
                </MotionButton>
              </div>

              <p className="cv3-legal">
                By submitting you agree to our terms and privacy policy.
              </p>
            </div>
          </div>
        </section>

        
      </div>
    </div>
  );
}

// Workflow illustration component
function WorkflowIllustration() {
  return (
    <svg
      width="100%"
      height="350"
      viewBox="0 0 400 350"
      fill="none"
      style={{ borderRadius: '20px', border: '1px solid var(--ring)' }}
    >
      <rect width="400" height="350" fill="#F9FAFB" rx="20" />
      <defs>
        <linearGradient id="greenGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#10B981" />
          <stop offset="100%" stopColor="#059F46" />
        </linearGradient>
        <linearGradient id="orangeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FB923C" />
          <stop offset="100%" stopColor="#EA580C" />
        </linearGradient>
      </defs>

      {/* Green geometric shapes */}
      <path
        d="M20 50 L180 80 L160 200 L50 280 L20 150 Z"
        fill="url(#greenGrad)"
        opacity="0.9"
      />
      <path
        d="M30 60 L120 120 L80 220 L30 300 Z"
        fill="url(#greenGrad)"
        opacity="0.6"
      />

      {/* Orange geometric shapes */}
      <path
        d="M220 40 L380 70 L350 190 L270 270 L220 140 Z"
        fill="url(#orangeGrad)"
        opacity="0.9"
      />
      <path
        d="M230 50 L320 110 L280 210 L230 290 Z"
        fill="url(#orangeGrad)"
        opacity="0.6"
      />

      {/* Connecting curved flow */}
      <path
        d="M160 140 Q200 120 240 160 Q280 200 320 180"
        stroke="#6B7280"
        strokeWidth="3"
        fill="none"
        strokeOpacity="0.4"
        strokeLinecap="round"
      />
      <path
        d="M140 180 Q180 160 220 200 Q260 240 300 220"
        stroke="#6B7280"
        strokeWidth="2"
        fill="none"
        strokeOpacity="0.3"
        strokeLinecap="round"
      />
    </svg>
  );
}

// SVG wave component with three layered dashed lines.
function Waves() {
  const width = 1200;
  const height = 90;

  // Precompute three sine paths with slight phase/amp differences
  const paths = useMemo(() => {
    const make = (amp: number, freq: number, phase: number) => {
      const points: string[] = [];
      const steps = 200;
      for (let i = 0; i <= steps; i++) {
        const t = (i / steps) * (Math.PI * 2 * freq);
        const x = (i / steps) * width;
        const y = height / 2 + Math.sin(t + phase) * amp;
        points.push(`${x},${y}`);
      }
      return points.join(" ");
    };
    return [
      make(10, 1, 0),
      make(8, 1.2, Math.PI / 2),
      make(6, 0.8, Math.PI),
    ];
  }, []);

  return (
    <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img" aria-label="decorative sound waves">
      <polyline className="wave-line wave-1" points={paths[0]} />
      <polyline className="wave-line wave-2" points={paths[1]} />
      <polyline className="wave-line wave-3" points={paths[2]} />
    </svg>
  );
}

// Magic UI Orbiting Circles demo used in the details visual
function MagicOrbitingCircles() {
  return (
    <div className="relative h-full w-full overflow-hidden">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <OrbitingCircles iconSize={30}>
          <MagicIcons.googleDrive />
          <MagicIcons.notion />
          <MagicIcons.openai />
          <MagicIcons.salesforce />
          <MagicIcons.hubspot />
        </OrbitingCircles>
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <OrbitingCircles iconSize={30} radius={80} reverse speed={12}>
            <MagicIcons.atSign />
            <MagicIcons.telephone />
            <MagicIcons.globe />
            <MagicIcons.document />
          </OrbitingCircles>
        </div>
      </div>
    </div>
  );
}

// Magic UI Animated Beam demo - using reference implementation
function MagicAnimatedBeam() {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const div1Ref = React.useRef<HTMLDivElement>(null);
  const div2Ref = React.useRef<HTMLDivElement>(null);
  const div3Ref = React.useRef<HTMLDivElement>(null);
  const div4Ref = React.useRef<HTMLDivElement>(null);
  const div5Ref = React.useRef<HTMLDivElement>(null);
  const div6Ref = React.useRef<HTMLDivElement>(null);
  const div7Ref = React.useRef<HTMLDivElement>(null);

  const Circle = React.forwardRef<HTMLDivElement, { className?: string; children?: React.ReactNode }>(
    ({ className, children }, ref) => (
      <div
        ref={ref}
        className={cn(
          "z-10 flex size-12 items-center justify-center rounded-full border-2 bg-white p-3 shadow-[0_0_20px_-12px_rgba(0,0,0,0.8)]",
          className,
        )}
      >
        {children}
      </div>
    )
  );
  Circle.displayName = 'Circle';

  return (
    <div
      className="relative flex h-[300px] w-full items-center justify-center overflow-hidden p-10"
      ref={containerRef}
    >
      <div className="flex size-full max-h-[200px] max-w-lg flex-col items-stretch justify-between gap-10">
        <div className="flex flex-row items-center justify-between">
          <Circle ref={div1Ref}>
            <MagicIcons.salesforce />
          </Circle>
          <Circle ref={div5Ref}>
            <MagicIcons.document />
          </Circle>
        </div>
        <div className="flex flex-row items-center justify-between">
          <Circle ref={div2Ref}>
            <MagicIcons.telephone />
          </Circle>
          <Circle ref={div4Ref} className="size-16">
            <MagicIcons.openai />
          </Circle>
          <Circle ref={div6Ref}>
            <MagicIcons.globe />
          </Circle>
        </div>
        <div className="flex flex-row items-center justify-between">
          <Circle ref={div3Ref}>
            <MagicIcons.atSign />
          </Circle>
          <Circle ref={div7Ref}>
            <MagicIcons.hubspot />
          </Circle>
        </div>
      </div>

      <AnimatedBeam
        containerRef={containerRef}
        fromRef={div1Ref}
        toRef={div4Ref}
        curvature={-75}
        endYOffset={-10}
        reverse
        />
      <AnimatedBeam
        containerRef={containerRef}
        fromRef={div2Ref}
        toRef={div4Ref}
        reverse        
      />
      <AnimatedBeam
        containerRef={containerRef}
        fromRef={div3Ref}
        toRef={div4Ref}
        curvature={75}
        endYOffset={10}
        reverse        
      />
      <AnimatedBeam
        containerRef={containerRef}
        fromRef={div5Ref}
        toRef={div4Ref}
        curvature={-75}
        endYOffset={-10}
        reverse
      />
      <AnimatedBeam
        containerRef={containerRef}
        fromRef={div6Ref}
        toRef={div4Ref}
        reverse        
      />
      <AnimatedBeam
        containerRef={containerRef}
        fromRef={div7Ref}
        toRef={div4Ref}
        curvature={75}
        endYOffset={10}
        reverse
/>
    </div>
  );
}

const MagicIcons = {
  notion: () => (
    <svg width="32" height="32" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.017 4.313l55.333 -4.087c6.797 -0.583 8.543 -0.19 12.817 2.917l17.663 12.443c2.913 2.14 3.883 2.723 3.883 5.053v68.243c0 4.277 -1.553 6.807 -6.99 7.193L24.467 99.967c-4.08 0.193 -6.023 -0.39 -8.16 -3.113L3.3 79.94c-2.333 -3.113 -3.3 -5.443 -3.3 -8.167V11.113c0 -3.497 1.553 -6.413 6.017 -6.8z" fill="#ffffff"/>
      <path d="M105.35 0.227l-55.333 4.087C1.553 4.7 0 7.617 0 11.113v60.66c0 2.723 0.967 5.053 3.3 8.167l13.007 16.913c2.137 2.723 4.08 3.307 8.16 3.113l64.257 -3.89c5.433 -0.387 6.99 -2.917 6.99 -7.193V20.64c0 -2.21 -0.873 -2.847 -3.443 -4.733L74.167 3.143c-4.273 -3.107 -6.02 -3.5 -12.817 -2.917zM25.92 19.523c-5.247 0.353 -6.437 0.433 -9.417 -1.99L8.927 11.507c-0.77 -0.78 -0.383 -1.753 1.557 -1.947l53.193 -3.887c4.467 -0.39 6.793 1.167 8.54 2.527l9.123 6.61c0.39 0.197 1.36 1.36 0.193 1.36l-54.933 3.307 -0.68 0.047zM19.803 88.3V30.367c0 -2.53 0.777 -3.697 3.103 -3.893L86 22.78c2.14 -0.193 3.107 1.167 3.107 3.693v57.547c0 2.53 -0.39 4.67 -3.883 4.863l-60.377 3.5c-3.493 0.193 -5.043 -0.97 -5.043 -4.083zm59.6 -54.827c0.387 1.75 0 3.5 -1.75 3.7l-2.91 0.577v42.773c-2.527 1.36 -4.853 2.137 -6.797 2.137 -3.107 0 -3.883 -0.973 -6.21 -3.887l-19.03 -29.94v28.967l6.02 1.363s0 3.5 -4.857 3.5l-13.39 0.777c-0.39 -0.78 0 -2.723 1.357 -3.11l3.497 -0.97v-38.3L30.48 40.667c-0.39 -1.75 0.58 -4.277 3.3 -4.473l14.367 -0.967 19.8 30.327v-26.83l-5.047 -0.58c-0.39 -2.143 1.163 -3.7 3.103 -3.89l13.4 -0.78z" fill="#000000" fillRule="evenodd" clipRule="evenodd"/>
    </svg>
  ),
  openai: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="fill-black">
      <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
    </svg>
  ),
  googleDrive: () => (
    <svg width="32" height="32" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
      <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da" />
      <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47" />
      <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335" />
      <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d" />
      <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc" />
      <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00" />
    </svg>
  ),
  whatsapp: () => (
    <svg width="32" height="32" viewBox="0 0 175.216 175.552" xmlns="http://www.w3.org/2000/svg">
      <path d="m12.966 161.238 10.439-38.114a73.42 73.42 0 0 1-9.821-36.772c.017-40.556 33.021-73.55 73.578-73.55 19.681.01 38.154 7.669 52.047 21.572s21.537 32.383 21.53 52.037c-.018 40.553-33.027 73.553-73.578 73.553h-.032c-12.313-.005-24.412-3.094-35.159-8.954z" fill="#ffffff" />
      <path d="M68.772 55.603c-1.378-3.061-2.828-3.123-4.137-3.176l-3.524-.043c-1.226 0-3.218.46-4.902 2.3s-6.435 6.287-6.435 15.332 6.588 17.785 7.506 19.013 12.718 20.381 31.405 27.75c15.529 6.124 18.689 4.906 22.061 4.6s10.877-4.447 12.408-8.74 1.532-7.971 1.073-8.74-1.685-1.226-3.525-2.146-10.877-5.367-12.562-5.981-2.91-.919-4.137.921-4.746 5.979-5.819 7.206-2.144 1.381-3.984.462-7.76-2.861-14.784-9.124c-5.465-4.873-9.154-10.891-10.228-12.73s-.114-2.835.808-3.751c.825-.824 1.838-2.147 2.759-3.22s1.224-1.84 1.836-3.065.307-2.301-.153-3.22-4.032-10.011-5.666-13.647" fill="#23b33a" />
    </svg>
  ),
  googleDocs: () => (
    <svg width="22" height="30" viewBox="0 0 47 65" xmlns="http://www.w3.org/2000/svg"><path d="M29.375,0 L4.40625,0 C1.9828125,0 0,1.99431818 0,4.43181818 L0,60.5681818 C0,63.0056818 1.9828125,65 4.40625,65 L42.59375,65 C45.0171875,65 47,63.0056818 47,60.5681818 L47,17.7272727 L29.375,0 Z" fill="#4285F4"/></svg>
  ),
  salesforce: () => (
    <svg width="32" height="32" viewBox="0 0 92 64" xmlns="http://www.w3.org/2000/svg">
      <g fill="none" fillRule="evenodd">
        <path fill="#00A1E0" d="M38.05 6.98c2.948-3.071 7.055-4.978 11.595-4.978 6.035 0 11.302 3.366 14.106 8.363a19.494 19.494 0 0 1 7.974-1.695c10.886 0 19.71 8.902 19.71 19.885 0 10.983-8.824 19.885-19.71 19.885-1.331 0-2.629-.133-3.884-.386-2.469 4.403-7.177 7.379-12.578 7.379-2.261 0-4.4-.52-6.303-1.451C46.456 59.872 40.623 64 33.826 64c-7.078 0-13.112-4.48-15.427-10.761a15.176 15.176 0 0 1-3.137.327C6.833 53.567 0 46.663 0 38.146a15.442 15.442 0 0 1 7.631-13.357 17.662 17.662 0 0 1-1.46-7.053C6.171 7.94 14.122 0 23.93 0a17.73 17.73 0 0 1 14.12 6.98"/>
        <path fill="#FFF" d="M13.244 33.19l.37-1.027c.059-.176.192-.118.246-.085.103.061.177.116.31.194 1.09.689 2.1.696 2.415.696.816 0 1.322-.432 1.322-1.015v-.03c0-.634-.78-.874-1.681-1.15l-.2-.064c-1.237-.352-2.56-.861-2.56-2.427v-.032c0-1.486 1.199-2.523 2.915-2.523l.188-.002c1.008 0 1.982.293 2.688.721.064.04.126.114.09.212l-.38 1.027c-.067.175-.25.059-.25.059a5.41 5.41 0 0 0-2.382-.611c-.728 0-1.196.386-1.196.91v.033c0 .611.802.872 1.732 1.175l.16.05c1.233.39 2.549.93 2.549 2.415v.031c0 1.605-1.166 2.602-3.041 2.602-.921 0-1.802-.142-2.734-.637-.176-.102-.35-.19-.522-.315-.018-.026-.097-.057-.04-.207zm27.457 0l.371-1.027c.054-.168.211-.106.245-.085.102.063.178.116.31.194 1.092.689 2.1.696 2.418.696.813 0 1.32-.432 1.32-1.015v-.03c0-.634-.779-.874-1.68-1.15l-.2-.064c-1.239-.352-2.562-.861-2.562-2.427v-.032c0-1.486 1.2-2.523 2.916-2.523l.187-.002c1.008 0 1.983.293 2.69.721.062.04.125.114.09.212-.035.091-.347.931-.38 1.027-.069.175-.25.059-.25.059a5.41 5.41 0 0 0-2.383-.611c-.728 0-1.196.386-1.196.91v.033c0 .611.801.872 1.732 1.175l.16.05c1.233.39 2.548.93 2.548 2.415v.031c0 1.605-1.165 2.602-3.04 2.602-.922 0-1.803-.142-2.734-.637-.176-.102-.35-.19-.523-.315-.018-.026-.097-.057-.039-.207zm20.31-4.829c.154.516.23 1.083.23 1.682 0 .6-.076 1.165-.23 1.681a3.77 3.77 0 0 1-.71 1.361 3.384 3.384 0 0 1-1.204.906c-.48.22-1.044.33-1.678.33-.634 0-1.2-.11-1.678-.33a3.384 3.384 0 0 1-1.204-.906 3.793 3.793 0 0 1-.711-1.36 5.909 5.909 0 0 1-.23-1.682c0-.6.077-1.166.23-1.682.154-.52.393-.978.71-1.36a3.442 3.442 0 0 1 1.205-.914c.479-.224 1.042-.337 1.678-.337.636 0 1.199.113 1.678.337.478.223.884.53 1.204.914.318.382.558.84.71 1.36zm-1.564 1.682c0-.907-.168-1.62-.502-2.12-.33-.496-.83-.736-1.526-.736-.696 0-1.192.24-1.518.736-.327.5-.494 1.213-.494 2.12 0 .906.167 1.624.496 2.128.324.502.82.745 1.516.745.696 0 1.196-.244 1.526-.745.332-.504.502-1.222.502-2.128zm14.422 2.63l.384 1.062c.05.13-.063.187-.063.187-.593.23-1.416.394-2.217.394-1.358 0-2.398-.391-3.092-1.163-.69-.77-1.042-1.817-1.042-3.116 0-.601.087-1.17.257-1.685.17-.52.425-.978.761-1.36a3.613 3.613 0 0 1 1.261-.914c.5-.223 1.088-.335 1.744-.335.443 0 .837.027 1.175.077.361.056.842.186 1.045.265.037.014.14.064.098.185-.148.417-.249.689-.386 1.069-.06.162-.182.108-.182.108-.515-.162-1.009-.236-1.654-.236-.775 0-1.357.258-1.737.763-.384.509-.599 1.176-.602 2.063-.003.973.241 1.694.673 2.14.431.445 1.033.67 1.791.67.307 0 .597-.02.858-.061.258-.041.5-.121.728-.21 0 0 .147-.055.2.097zm8.01-4.607c.341 1.194.163 2.225.157 2.282-.013.136-.153.138-.153.138l-5.299-.004c.033.805.226 1.375.616 1.762.383.379.991.622 1.814.623 1.258.003 1.795-.25 2.176-.391 0 0 .145-.052.2.092l.345.971c.07.163.014.22-.045.253-.332.183-1.137.525-2.669.529-.743.003-1.39-.103-1.923-.31a3.502 3.502 0 0 1-1.333-.883 3.497 3.497 0 0 1-.769-1.347 5.713 5.713 0 0 1-.239-1.692c0-.6.077-1.17.232-1.691.155-.525.396-.988.717-1.377a3.479 3.479 0 0 1 1.221-.93c.486-.229 1.088-.341 1.75-.341.567 0 1.085.122 1.516.308.332.142.666.399 1.008.767.216.232.545.74.678 1.241zm-5.27 1.107h3.78c-.039-.486-.134-.922-.352-1.25-.332-.496-.79-.769-1.485-.769-.696 0-1.19.273-1.517.769-.214.328-.352.746-.427 1.25zm-37.175-1.107c.34 1.194.165 2.225.159 2.282-.014.136-.154.138-.154.138l-5.3-.004c.034.805.226 1.375.617 1.762.383.379.99.622 1.813.623 1.258.003 1.797-.25 2.177-.391 0 0 .145-.052.199.092l.346.971c.07.163.014.22-.044.253-.334.183-1.14.525-2.67.529-.744.003-1.391-.103-1.923-.31a3.514 3.514 0 0 1-1.334-.883 3.506 3.506 0 0 1-.767-1.347 5.68 5.68 0 0 1-.241-1.692c0-.6.078-1.17.232-1.691a3.88 3.88 0 0 1 .718-1.377 3.488 3.488 0 0 1 1.22-.93c.488-.229 1.09-.341 1.75-.341a3.85 3.85 0 0 1 1.518.308c.332.142.666.399 1.007.767.216.232.545.74.677 1.241zm-5.271 1.107h3.782c-.04-.486-.135-.922-.352-1.25-.33-.496-.79-.769-1.485-.769-.696 0-1.191.273-1.516.769-.216.328-.353.746-.43 1.25zm-9.346-.253s.418.037.874.103v-.224c0-.707-.147-1.04-.436-1.263-.296-.226-.738-.343-1.31-.343 0 0-1.29-.016-2.31.538-.047.028-.086.044-.086.044s-.128.045-.174-.086L21 26.681c-.058-.145.047-.211.047-.211.477-.372 1.633-.597 1.633-.597a8.711 8.711 0 0 1 1.421-.131c1.058 0 1.877.246 2.434.733.558.489.842 1.277.842 2.339l.003 4.848s.011.14-.122.172c0 0-.195.054-.37.095-.177.041-.815.171-1.336.259a9.744 9.744 0 0 1-1.61.134c-.515 0-.987-.048-1.403-.143a2.92 2.92 0 0 1-1.079-.468 2.192 2.192 0 0 1-.69-.829c-.163-.333-.245-.74-.245-1.21 0-.461.097-.872.284-1.222.188-.348.446-.643.77-.874a3.425 3.425 0 0 1 1.105-.513c.413-.11.852-.167 1.306-.167.333 0 .611.007.827.024zm-2.109 3.724c-.003-.001.475.375 1.554.309.758-.046 1.43-.19 1.43-.19v-2.41s-.678-.111-1.439-.122c-1.079-.013-1.539.384-1.536.383-.318.226-.473.561-.473 1.025 0 .297.053.529.16.691.067.107.096.147.304.314zm44.855-6.455c-.05.145-.307.871-.4 1.112-.034.092-.09.155-.193.144 0 0-.304-.07-.582-.07-.191 0-.464.024-.71.1a1.586 1.586 0 0 0-.654.393c-.194.187-.351.45-.465.78-.116.332-.176.86-.176 1.39v3.948a.16.16 0 0 1-.16.161h-1.391a.162.162 0 0 1-.164-.16v-7.905c0-.089.065-.16.154-.16h1.357c.09 0 .154.071.154.16v.646c.203-.272.567-.512.896-.66.33-.15.699-.26 1.366-.22.347.021.798.116.889.151a.144.144 0 0 1 .079.19zm-13.07-3.663c.037.015.138.064.098.184l-.407 1.113c-.034.084-.056.134-.229.082a2.388 2.388 0 0 0-.707-.11c-.21 0-.4.027-.568.083a1.084 1.084 0 0 0-.444.274 1.545 1.545 0 0 0-.372.566c-.195.56-.27 1.157-.28 1.195h1.694c.143 0 .188.066.174.171l-.198 1.102c-.032.16-.177.154-.177.154h-1.746l-1.193 6.756a10.426 10.426 0 0 1-.466 1.78c-.187.489-.38.846-.69 1.187a2.43 2.43 0 0 1-.979.682c-.367.135-.813.204-1.3.204-.232 0-.482-.005-.777-.075a4.198 4.198 0 0 1-.485-.142c-.065-.023-.118-.106-.08-.212.036-.105.35-.964.392-1.077.054-.136.192-.084.192-.084.094.04.16.066.285.091.127.025.297.047.426.047.231 0 .441-.028.624-.09.22-.071.35-.2.484-.372.14-.181.254-.426.371-.755.117-.333.224-.773.316-1.306l1.188-6.633h-1.17c-.14 0-.188-.066-.172-.172l.196-1.102c.031-.16.18-.154.18-.154h1.201l.065-.358c.18-1.064.537-1.873 1.065-2.404.531-.535 1.286-.805 2.244-.805.274 0 .516.018.721.055.201.038.354.073.524.125zM30.817 33.986c0 .09-.062.161-.152.161H29.26c-.09 0-.151-.072-.151-.16V22.674c0-.087.062-.159.15-.159h1.406c.09 0 .152.072.152.16v11.311z"/>
      </g>
    </svg>
  ),
  hubspot: () => (
    <svg width="32" height="32" viewBox="6.20856283 .64498824 244.26943717 251.24701176" xmlns="http://www.w3.org/2000/svg">
      <path d="m191.385 85.694v-29.506a22.722 22.722 0 0 0 13.101-20.48v-.677c0-12.549-10.173-22.722-22.721-22.722h-.678c-12.549 0-22.722 10.173-22.722 22.722v.677a22.722 22.722 0 0 0 13.101 20.48v29.506a64.342 64.342 0 0 0 -30.594 13.47l-80.922-63.03c.577-2.083.878-4.225.912-6.375a25.6 25.6 0 1 0 -25.633 25.55 25.323 25.323 0 0 0 12.607-3.43l79.685 62.007c-14.65 22.131-14.258 50.974.987 72.7l-24.236 24.243c-1.96-.626-4-.959-6.057-.987-11.607.01-21.01 9.423-21.007 21.03.003 11.606 9.412 21.014 21.018 21.017 11.607.003 21.02-9.4 21.03-21.007a20.747 20.747 0 0 0 -.988-6.056l23.976-23.985c21.423 16.492 50.846 17.913 73.759 3.562 22.912-14.352 34.475-41.446 28.985-67.918-5.49-26.473-26.873-46.734-53.603-50.792m-9.938 97.044a33.17 33.17 0 1 1 0-66.316c17.85.625 32 15.272 32.01 33.134.008 17.86-14.127 32.522-31.977 33.165" fill="#ff7a59"/>
    </svg>
  ),
  telephone: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="#4A5568">
      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
    </svg>
  ),
  atSign: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="#4A5568">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10c1.09 0 2-.89 2-2s-.91-2-2-2c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6v1.43c0 .79-.71 1.57-1.5 1.57s-1.5-.78-1.5-1.57V12c0-1.66-1.34-3-3-3s-3 1.34-3 3 1.34 3 3 3c.95 0 1.8-.45 2.35-1.15.35.72 1.09 1.15 1.9 1.15 1.66 0 3.5-1.34 3.5-3.57V12c0-5.52-4.48-10-10-10zm0 13c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/>
    </svg>
  ),
  document: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="#4A5568">
      <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.89 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/>
    </svg>
  ),
  globe: () => (
    <svg width="32" height="32" viewBox="0 0 3001 3001" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="nonzero" fill="rgb(6.459045%, 5.915833%, 5.220032%)" fillOpacity="1" d="M 422.460938 1457.488281 C 409.132812 1457.488281 396.238281 1458.78125 383.339844 1461.359375 C 380.761719 1431.699219 379.46875 1401.601562 379.46875 1371.511719 C 379.46875 1122.171875 459.859375 886.148438 612.046875 688.828125 C 626.664062 669.910156 653.746094 666.898438 672.230469 681.089844 C 691.148438 695.710938 694.589844 722.789062 679.96875 741.28125 C 539.820312 923.121094 465.449219 1141.078125 465.449219 1371.511719 C 465.449219 1400.308594 466.742188 1429.121094 469.320312 1457.488281 L 422.460938 1457.488281 "/>
      <path fillRule="nonzero" fill="rgb(6.459045%, 5.915833%, 5.220032%)" fillOpacity="1" d="M 2091.761719 2317.289062 C 2091.761719 2317.289062 2091.328125 2317.289062 2091.328125 2317.71875 C 1918.941406 2426.058594 1715.601562 2489.25 1497.210938 2489.25 C 1278.820312 2489.25 1075.050781 2426.058594 902.65625 2317.289062 L 1085.371094 2317.289062 C 1124.488281 2334.488281 1164.46875 2349.101562 1205.738281 2361.140625 C 1284.839844 2384.78125 1368.238281 2398.96875 1454.21875 2402.410156 C 1468.410156 2402.839844 1482.589844 2403.269531 1497.210938 2403.269531 C 1511.828125 2403.269531 1526.011719 2402.839844 1540.199219 2402.410156 C 1626.179688 2398.96875 1709.578125 2384.78125 1788.679688 2361.140625 C 1829.949219 2349.101562 1869.929688 2334.488281 1909.058594 2317.289062 L 2091.761719 2317.289062 "/>
      <path fillRule="nonzero" fill="rgb(6.459045%, 5.915833%, 5.220032%)" fillOpacity="1" d="M 2614.949219 1371.511719 C 2614.949219 1627.300781 2528.539062 1862.878906 2383.671875 2051.179688 L 2357.011719 2030.550781 L 2357.011719 1941.128906 C 2365.179688 1929.089844 2372.921875 1917.050781 2380.230469 1905.011719 C 2474.800781 1749.390625 2528.96875 1566.679688 2528.96875 1371.511719 C 2528.96875 903.78125 2216 507.839844 1788.679688 381.878906 C 1709.578125 358.230469 1626.179688 344.050781 1540.199219 340.609375 C 1526.011719 340.179688 1511.828125 339.75 1497.210938 339.75 C 1483.019531 339.75 1468.410156 340.179688 1454.21875 340.609375 C 1369.53125 344.050781 1286.128906 358.230469 1205.738281 381.878906 C 1182.519531 388.328125 1159.308594 396.058594 1136.519531 404.660156 C 1021.738281 447.21875 915.128906 510.421875 821.410156 591.671875 C 803.78125 607.140625 776.699219 605.429688 760.792969 587.371094 C 745.316406 569.320312 747.464844 542.230469 765.089844 526.75 C 968.433594 350.488281 1228.089844 253.769531 1497.210938 253.769531 C 1805.449219 253.769531 2084.878906 379.300781 2287.371094 581.351562 C 2489.421875 783.839844 2614.949219 1063.269531 2614.949219 1371.511719 "/>
      <path fillRule="nonzero" fill="rgb(6.459045%, 5.915833%, 5.220032%)" fillOpacity="1" d="M 2571.960938 1414.5 L 422.460938 1414.5 L 422.460938 1328.519531 L 2571.960938 1328.519531 L 2571.960938 1414.5 "/>
      <path fillRule="nonzero" fill="rgb(6.459045%, 5.915833%, 5.220032%)" fillOpacity="1" d="M 2418.429688 838.730469 L 575.988281 838.730469 L 575.988281 752.75 L 2418.429688 752.75 L 2418.429688 838.730469 "/>
      <path fillRule="nonzero" fill="rgb(6.459045%, 5.915833%, 5.220032%)" fillOpacity="1" d="M 1540.199219 296.761719 L 1540.199219 1457.488281 L 1454.21875 1457.488281 L 1454.21875 296.761719 L 1540.199219 296.761719 "/>
      <path fillRule="nonzero" fill="rgb(6.459045%, 5.915833%, 5.220032%)" fillOpacity="1" d="M 815.078125 2102.339844 C 793.433594 2102.339844 774.25 2089.050781 767.132812 2069.070312 L 698.785156 1876.679688 L 630.457031 2069.039062 C 623.257812 2089.28125 603.003906 2101.351562 581.71875 2102.339844 C 559.78125 2102 540.636719 2088.078125 534.066406 2067.679688 L 424.554688 1728.628906 C 417.25 1706.039062 429.65625 1681.820312 452.265625 1674.511719 C 474.914062 1667.289062 499.074219 1679.609375 506.378906 1702.21875 L 584.066406 1942.761719 L 650.820312 1754.851562 C 657.9375 1734.910156 677.207031 1721.550781 698.785156 1721.550781 C 720.363281 1721.550781 739.632812 1734.910156 746.730469 1754.78125 L 813.503906 1942.738281 L 891.191406 1702.21875 C 898.476562 1679.609375 922.699219 1667.289062 945.304688 1674.511719 C 967.914062 1681.820312 980.320312 1706.039062 973.015625 1728.628906 L 863.503906 2067.699219 C 856.933594 2088.078125 837.789062 2102 815.875 2102.339844 C 815.601562 2102.339844 815.351562 2102.339844 815.078125 2102.339844 "/>
      <path fillRule="nonzero" fill="rgb(6.459045%, 5.915833%, 5.220032%)" fillOpacity="1" d="M 1398.359375 2102.339844 C 1376.71875 2102.339844 1357.53125 2089.050781 1350.421875 2069.070312 L 1282.070312 1876.660156 L 1213.71875 2069.070312 C 1206.519531 2089.300781 1187.570312 2102.589844 1164.980469 2102.339844 C 1143.058594 2102 1123.921875 2088.078125 1117.351562 2067.679688 L 1007.839844 1728.628906 C 1000.53125 1706.039062 1012.941406 1681.820312 1035.550781 1674.511719 C 1058.148438 1667.289062 1082.378906 1679.609375 1089.660156 1702.21875 L 1167.351562 1942.738281 L 1234.101562 1754.828125 C 1241.21875 1734.910156 1260.488281 1721.550781 1282.070312 1721.550781 C 1303.648438 1721.550781 1322.921875 1734.910156 1330.011719 1754.78125 L 1396.789062 1942.738281 L 1474.46875 1702.21875 C 1481.78125 1679.609375 1505.921875 1667.289062 1528.589844 1674.511719 C 1551.199219 1681.820312 1563.601562 1706.039062 1556.300781 1728.628906 L 1446.789062 2067.699219 C 1440.21875 2088.078125 1421.070312 2102 1399.160156 2102.339844 C 1398.878906 2102.339844 1398.628906 2102.339844 1398.359375 2102.339844 "/>
      <path fillRule="nonzero" fill="rgb(6.459045%, 5.915833%, 5.220032%)" fillOpacity="1" d="M 1981.640625 2102.339844 C 1960 2102.339844 1940.789062 2089.050781 1933.679688 2069.070312 L 1865.351562 1876.679688 L 1797 2069.070312 C 1789.800781 2089.300781 1770.828125 2102.589844 1748.261719 2102.339844 C 1726.351562 2102 1707.199219 2088.078125 1700.628906 2067.679688 L 1591.121094 1728.628906 C 1583.820312 1706.039062 1596.21875 1681.820312 1618.828125 1674.511719 C 1641.5 1667.289062 1665.660156 1679.609375 1672.949219 1702.21875 L 1750.628906 1942.738281 L 1817.390625 1754.828125 C 1824.5 1734.910156 1843.769531 1721.550781 1865.351562 1721.550781 C 1886.929688 1721.550781 1906.199219 1734.910156 1913.300781 1754.78125 L 1980.070312 1942.761719 L 2057.761719 1702.21875 C 2065.039062 1679.609375 2089.269531 1667.289062 2111.871094 1674.511719 C 2134.480469 1681.820312 2146.890625 1706.039062 2139.578125 1728.628906 L 2030.070312 2067.699219 C 2023.5 2088.078125 2004.359375 2102 1982.421875 2102.339844 C 1982.171875 2102.339844 1981.898438 2102.339844 1981.640625 2102.339844 "/>
    </svg>
  ),
};
